import React, { useEffect, useState } from 'react';
import { PLATFORM_SHORT_NAME } from '../branding';

// ── Install Prompt ─────────────────────────────────────────────────────────────

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/** Suppress the prompt for a month once someone says "not now". */
const SNOOZE_KEY = 'installPromptSnoozedUntil';
const SNOOZE_DAYS = 30;

const isSnoozed = () => {
  const until = Number(localStorage.getItem(SNOOZE_KEY) || 0);
  return until > Date.now();
};

const snooze = () =>
  localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_DAYS * 24 * 60 * 60 * 1000));

/** Running as an installed app rather than in a browser tab. */
export const isStandalone = (): boolean =>
  window.matchMedia('(display-mode: standalone)').matches ||
  // iOS predates the display-mode media query and uses its own flag.
  (window.navigator as any).standalone === true;

const isIos = (): boolean =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) ||
  // iPadOS 13+ reports itself as a Mac; the touch points give it away.
  (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);

export const InstallPrompt: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);
  // iOS has no install API at all — Safari never fires beforeinstallprompt —
  // so the only thing we can do there is show the user where the button is.
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || isSnoozed()) return;

    if (isIos()) {
      // Add to Home Screen exists only in Safari; in Chrome or Firefox on iOS
      // the option isn't there, so telling the user to look for it would send
      // them hunting for a menu item that doesn't exist.
      const ua = window.navigator.userAgent;
      const isSafari = /safari/i.test(ua) && !/crios|fxios|edgios/i.test(ua);
      if (isSafari) setIosHint(true);
      return;
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const dismiss = () => {
    snooze();
    setVisible(false);
    setIosHint(false);
  };

  if (iosHint) {
    return (
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-full max-w-sm px-4">
        <div className="bg-[#1e3a5f] text-white rounded-2xl shadow-2xl p-4">
          <div className="flex items-start gap-3">
            <div className="text-3xl shrink-0">📲</div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm">Add {PLATFORM_SHORT_NAME} to Home Screen</p>
              <p className="text-xs text-blue-200 mt-1 leading-relaxed">
                Tap the Share button{' '}
                <span aria-hidden="true" className="inline-block px-1">
                  ⬆️
                </span>{' '}
                at the bottom of Safari, then choose <strong>Add to Home Screen</strong>.
              </p>
            </div>
            <button
              onClick={dismiss}
              aria-label="Dismiss"
              className="text-blue-300 hover:text-white text-lg leading-none shrink-0"
            >
              ×
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') setVisible(false);
    setDeferredPrompt(null);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-full max-w-sm px-4">
      <div className="bg-[#1e3a5f] text-white rounded-2xl shadow-2xl p-4 flex items-center gap-3">
        <div className="text-3xl shrink-0">📲</div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">Add {PLATFORM_SHORT_NAME} to Home Screen</p>
          <p className="text-xs text-blue-200 mt-0.5">Quick access, works offline, no browser bar</p>
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          <button
            onClick={handleInstall}
            className="bg-white text-[#1e3a5f] text-xs font-bold px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-colors"
          >
            Install
          </button>
          <button onClick={dismiss} className="text-blue-300 text-xs hover:text-white text-center">
            Not now
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Update Banner ──────────────────────────────────────────────────────────────

export const UpdateBanner: React.FC = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handler = () => setVisible(true);
    window.addEventListener('sw-update-available', handler);
    return () => window.removeEventListener('sw-update-available', handler);
  }, []);

  const handleRefresh = () => {
    // Tell the waiting service worker to activate immediately
    navigator.serviceWorker.getRegistration().then((reg) => {
      reg?.waiting?.postMessage({ type: 'SKIP_WAITING' });
    });
    window.location.reload();
  };

  if (!visible) return null;

  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 w-full max-w-sm px-4">
      <div className="bg-green-600 text-white rounded-2xl shadow-2xl p-4 flex items-center gap-3">
        <div className="text-2xl shrink-0">🔄</div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">Update Available</p>
          <p className="text-xs text-green-100 mt-0.5">A new version of {PLATFORM_SHORT_NAME} is ready</p>
        </div>
        <button
          onClick={handleRefresh}
          className="bg-white text-green-700 text-xs font-bold px-3 py-1.5 rounded-lg hover:bg-green-50 transition-colors shrink-0"
        >
          Refresh
        </button>
      </div>
    </div>
  );
};
